import { useEffect, useState } from "react";

import type { Method } from "axios";

import {
  Button,
  Input,
  Select,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { useLocalStorage } from "~/v2/hooks/useLocalStorage";
import { cn } from "~/v2/utils/cn";
import instance from "../../api";
import { useGetOwnerDetails } from "../../api/hooks";
import type { Account, OwnerDetails } from "../../api/rest/account";
import { useAccountContext } from "../../providers/AccountProvider";

type ApiSurface = "internal" | "trading" | "tradingInternal";

const API_SURFACES: Record<ApiSurface, { label: string; baseURL: string }> = {
  internal: {
    label: "gobroker (/internal)",
    baseURL: "/internal",
  },
  trading: {
    label: "trading-api service (/api/v1)",
    baseURL: "/api/v1",
  },
  tradingInternal: {
    label: "trading-api internal (/api/v1/_internal)",
    baseURL: "/api/v1/_internal",
  },
};

interface Preset {
  label: string;
  surface?: ApiSurface;
  config: (
    account: Account,
    details: OwnerDetails,
  ) => {
    method: Method;
    url: string;
    body: object;
  };
}

const PRESETS: Preset[] = [
  {
    label: "Create IRA Account",
    config: (_account, details) => ({
      method: "POST",
      url: "/accounts",
      body: {
        account_type: "ira",
        account_sub_type: "traditional",
        primary_account_holder_id: details.owner_id,
      },
    }),
  },
  {
    label: "Search Assets (Full Text)",
    config: () => ({
      method: "GET",
      url: "/assets/search?query=AAPL&limit=10&offset=0&status=active",
      body: {},
    }),
  },
  {
    label: "Get Market Status (NMS)",
    config: () => ({
      method: "GET",
      url: "/markets/NMS/status",
      body: {},
    }),
  },
  {
    label: "[Trading API] Whoami",
    surface: "trading",
    config: () => ({
      method: "GET",
      url: "/whoami",
      body: {},
    }),
  },
  {
    label: "[Trading API Internal] Account Margin",
    surface: "tradingInternal",
    config: () => ({
      method: "GET",
      url: "/accounts/{account_id}/trade_account/margin?include_recent_deposits=true",
      body: {},
    }),
  },
];

export const EndpointTesting = () => {
  const { currentAccount } = useAccountContext();

  const { details } = useGetOwnerDetails();

  const [endpointTestingOpen, setEndpointTestingOpen] =
    useLocalStorage<boolean>("devtools-endpoint-testing-open");

  const [surface, setSurface] = useState<ApiSurface>("internal");
  const [body, setBody] = useState<string>("{\n\n}");
  const [method, setMethod] = useState<Method>("GET");
  const [response, setResponse] = useState<string>("<response>");
  const [url, setUrl] = useState<string>("");

  const transformedUrl = url.replaceAll(
    "{account_id}",
    currentAccount?.id ?? "",
  );

  const activeBaseURL = API_SURFACES[surface].baseURL;

  const reset = () => {
    setBody("{\n\n}");
    setMethod("GET");
    setUrl("");
    setResponse("<response>");
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentAccount is the trigger: the request/response panes are cleared when the account changes. reset is a plain local function redefined every render, so listing it would clear the panes on every render.
  useEffect(() => {
    reset();
  }, [currentAccount]);

  const handleSubmit = async () => {
    try {
      const res = await instance.request({
        method,
        baseURL: activeBaseURL,
        url: transformedUrl,
        data: JSON.parse(body),
        validateStatus: () => true,
      });
      if (res.status >= 200 && res.status < 300) {
        toastSuccess(`Request succeeded with status code ${res.status}`, {});
      } else {
        toastError(`Request failed with status code ${res.status}`, {});
      }
      setResponse(JSON.stringify(res, null, 2));
    } catch (error) {
      setResponse(JSON.stringify(error, null, 2));
    }
  };

  return (
    <div className="overflow-hidden rounded border border-gray-300">
      <button
        type="button"
        onClick={() => setEndpointTestingOpen(!(endpointTestingOpen ?? true))}
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 transition-colors hover:bg-gray-100"
      >
        <h2 className="m-0 font-semibold text-lg">Endpoint Testing</h2>
        <span className="text-gray-500">
          {(endpointTestingOpen ?? true) ? "−" : "+"}
        </span>
      </button>
      {(endpointTestingOpen ?? true) && (
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-row gap-2">
            <Button onClick={reset}>Reset</Button>
            <Select
              placeholder="Select a preset"
              options={PRESETS.map((preset) => ({
                label: preset.label,
                value: preset,
              }))}
              value={null}
              onChange={(p) => {
                if (p) {
                  if (p.surface) {
                    setSurface(p.surface);
                  }
                  if (!currentAccount || !details) {
                    toastError(
                      "Select an account and wait for owner details to load",
                    );
                    return;
                  }
                  const { method, url, body } = p.config(
                    currentAccount,
                    details,
                  );
                  setMethod(method);
                  setUrl(url);
                  setBody(JSON.stringify(body, null, 2));
                }
              }}
            />
          </div>
          <div className="flex flex-row items-center gap-2">
            <span className="whitespace-nowrap font-medium text-gray-600 text-sm">
              API Surface:
            </span>
            {(
              Object.entries(API_SURFACES) as [
                ApiSurface,
                (typeof API_SURFACES)[ApiSurface],
              ][]
            ).map(([key, { label }]) => (
              <button
                type="button"
                key={key}
                onClick={() => setSurface(key)}
                className={cn(
                  "rounded border px-3 py-1 text-sm",
                  surface === key
                    ? "border-yellow-400 bg-yellow-100 font-semibold"
                    : "border-gray-300 bg-white hover:bg-gray-50",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex w-full flex-row gap-2">
            <Select
              onChange={(m) => setMethod(m as Method)}
              options={["GET", "POST", "PATCH", "PUT", "DELETE"].map(
                (method) => ({
                  label: method,
                  value: method,
                }),
              )}
              value={method}
            />
            <div className="grow">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter request URL"
              />
            </div>
            <Button onClick={handleSubmit}>Submit</Button>
          </div>
          <small className="text-gray-500">
            {activeBaseURL}
            {transformedUrl}
          </small>
          <textarea
            value={body}
            disabled={method === "GET" || method === "DELETE"}
            onChange={(e) => setBody(e.target.value)}
            className="h-32 w-full disabled:bg-gray-100"
          />
          <div>
            <h3 className="mb-2 font-semibold">Response</h3>
            <pre className="overflow-auto rounded bg-gray-50 p-2 text-sm">
              {response}
            </pre>
          </div>
          <div>
            <h3 className="mb-2 font-semibold">Account</h3>
            <pre className="overflow-auto rounded bg-gray-50 p-2 text-sm">
              {JSON.stringify(currentAccount, null, 2)}
            </pre>
          </div>
          <div>
            <h3 className="mb-2 font-semibold">Owner Details</h3>
            <pre className="overflow-auto rounded bg-gray-50 p-2 text-sm">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};
